import { createRemoteSandboxEnv, registerSandboxBackendFactory, registerSandboxRefDecoder, unregisterSandboxBackendFactory, unregisterSandboxRefDecoder } from '@fabric-harness/sdk';
import type { RemoteSandboxApi, SandboxEnv, SandboxFactoryOptions } from '@fabric-harness/sdk';
import type { ColumnInfo, StatementExecutionClient, StatementResponse } from '@databricks/sdk-statementexecution/v1';
import { databricksCredentials, databricksIdentity, databricksPrincipalFromEnv, type DatabricksPrincipal } from './identity.js';
import { createDatabricksNativeClients } from './native-sdk.js';

/**
 * Sandbox backend that maps `exec(command)` to a Databricks SQL Statement
 * Execution API call against a SQL Warehouse. Useful for data agents whose
 * "shell" is a query interface.
 *
 * Behavior:
 * - `exec(sql)` runs the SQL synchronously (with a timeout) and returns the
 *   serialized result set as `stdout` (newline-delimited JSON rows by default,
 *   or CSV when `resultFormat: 'csv'`).
 * - File operations are intentionally minimal: the sandbox-virtual filesystem
 *   keeps a small in-memory map. SQL warehouses are not file servers; mount
 *   actual data via `databricksVolumeSource` from `@fabric-harness/connectors`.
 *
 * Usage:
 * ```ts
 * import { databricksSqlSandbox } from '@fabric-harness/databricks/sql-sandbox';
 *
 * const fabric = await init({
 *   sandbox: databricksSqlSandbox({
 *     host: process.env.DATABRICKS_HOST!,
 *     principal: { kind: 'pat', token: process.env.DATABRICKS_TOKEN! },
 *     warehouseId: process.env.DATABRICKS_WAREHOUSE_ID!,
 *   }),
 * });
 * ```
 */
export interface DatabricksSqlSandboxOptions {
  host: string;
  principal: DatabricksPrincipal;
  /** SQL Warehouse ID to execute against. */
  warehouseId: string;
  /** Optional Unity Catalog. */
  catalog?: string;
  /** Optional schema. */
  schema?: string;
  /** Result row serialization. Defaults to `'jsonl'`. */
  resultFormat?: 'jsonl' | 'csv';
  /** Wait timeout for the statement, in seconds (10–50). Defaults to `30`. */
  waitTimeoutSeconds?: number;
  /** Custom fetch implementation (testing, OBO tokens, etc.). */
  fetchImpl?: typeof fetch;
  /** Inject a pre-built native Statement Execution client. */
  client?: Pick<StatementExecutionClient, 'executeStatement'>;
}

export interface DatabricksSqlSandboxRefData {
  host: string;
  warehouseId: string;
  catalog?: string;
  schema?: string;
  resultFormat?: 'jsonl' | 'csv';
  waitTimeoutSeconds?: number;
}

export interface DatabricksSqlSandboxRegistrationOptions {
  /** Resolve credentials at creation/attach time. Credentials are never stored in a sandbox ref. */
  resolvePrincipal?: (env: Record<string, string | undefined>, target: DatabricksSqlSandboxRefData) => DatabricksPrincipal;
  /** Inject a client for tests or a provider-owned transport. */
  client?: Pick<StatementExecutionClient, 'executeStatement'>;
}

const DATABRICKS_SQL_SANDBOX_REF_PROVIDER = 'databricks-sql';

export function databricksSqlSandbox(options: DatabricksSqlSandboxOptions): SandboxEnv {
  const identity = databricksIdentity(options.principal);
  const client = options.client ?? createDatabricksNativeClients({
    host: options.host,
    credentials: databricksCredentials(options.principal),
    tokenProvider: identity,
    ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
  }).statements;
  const waitTimeoutSeconds = options.waitTimeoutSeconds ?? 30;
  const resultFormat = options.resultFormat ?? 'jsonl';
  const memFs = new Map<string, Uint8Array>();

  const api: RemoteSandboxApi = {
    async exec(command) {
      const startedAt = Date.now();
      try {
        const response = await client.executeStatement({
          warehouseId: options.warehouseId,
          statement: command,
          waitTimeout: `${waitTimeoutSeconds}s`,
          onWaitTimeout: 'CANCEL',
          ...(options.catalog ? { catalog: options.catalog } : {}),
          ...(options.schema ? { schema: options.schema } : {}),
        });
        if (response.status?.state === 'FAILED' || response.status?.state === 'CANCELED') {
          return {
            command,
            exitCode: 1,
            stdout: '',
            stderr: response.status.error?.message ?? `Statement ${response.status.state}`,
            durationMs: Date.now() - startedAt,
          };
        }
        const stdout = serializeResult(response, resultFormat);
        return { command, exitCode: 0, stdout, stderr: '', durationMs: Date.now() - startedAt };
      } catch (error) {
        return {
          command,
          exitCode: 1,
          stdout: '',
          stderr: error instanceof Error ? error.message : String(error),
          durationMs: Date.now() - startedAt,
        };
      }
    },
    async readFile(path) {
      const bytes = memFs.get(path);
      if (!bytes) throw new Error(`No such file: ${path}`);
      return new TextDecoder().decode(bytes);
    },
    async readFileBuffer(path) {
      const bytes = memFs.get(path);
      if (!bytes) throw new Error(`No such file: ${path}`);
      return bytes;
    },
    async writeFile(path, content) {
      memFs.set(path, typeof content === 'string' ? new TextEncoder().encode(content) : content);
    },
    async stat(path) {
      const bytes = memFs.get(path);
      if (!bytes) throw new Error(`No such file: ${path}`);
      return { type: 'file', size: bytes.byteLength };
    },
    async readdir(path) {
      const prefix = path.replace(/\/$/, '');
      const direct = new Set<string>();
      for (const key of memFs.keys()) {
        if (!key.startsWith(`${prefix}/`)) continue;
        const tail = key.slice(prefix.length + 1);
        const slash = tail.indexOf('/');
        direct.add(slash > 0 ? tail.slice(0, slash) : tail);
      }
      return [...direct];
    },
    async exists(path) {
      return memFs.has(path);
    },
    async mkdir() { /* no-op for SQL sandbox; in-memory keys are flat */ },
    async rm(path) {
      for (const key of [...memFs.keys()]) {
        if (key === path || key.startsWith(`${path}/`)) memFs.delete(key);
      }
    },
  };
  return createRemoteSandboxEnv(api, {
    cwd: '/workspace',
    capabilities: { exec: true, filesystem: true, binaryFiles: true, snapshots: false, restore: false, reconnect: true, network: 'provider-managed', persistence: 'session', isolation: 'provider' },
    encodeRef: () => ({
      provider: DATABRICKS_SQL_SANDBOX_REF_PROVIDER,
      providerData: databricksSqlSandboxRefData(options),
    }),
  });
}

/** Register `sandbox: 'databricks'` and its credential-safe portable-ref decoder. */
export function registerDatabricksSqlSandboxBackend(registration: DatabricksSqlSandboxRegistrationOptions = {}): void {
  registerSandboxBackendFactory('databricks', (factoryOptions) => createRegisteredDatabricksSqlSandbox(factoryOptions, registration));
  registerSandboxRefDecoder(DATABRICKS_SQL_SANDBOX_REF_PROVIDER, (providerData) => {
    const target = assertDatabricksSqlSandboxRefData(providerData);
    return (factoryOptions) => databricksSqlSandbox({
      ...target,
      principal: resolveRegisteredPrincipal(factoryOptions, target, registration),
      ...(registration.client ? { client: registration.client } : {}),
    });
  });
}

/** Remove both process-local registrations, primarily for tests and controlled shutdown. */
export function unregisterDatabricksSqlSandboxBackend(): void {
  unregisterSandboxBackendFactory('databricks');
  unregisterSandboxRefDecoder(DATABRICKS_SQL_SANDBOX_REF_PROVIDER);
}

function createRegisteredDatabricksSqlSandbox(options: SandboxFactoryOptions, registration: DatabricksSqlSandboxRegistrationOptions): SandboxEnv {
  const env = options.env ?? process.env;
  const metadata = options.metadata ?? {};
  const catalog = metadataString(metadata, 'databricksCatalog') ?? env.DATABRICKS_CATALOG;
  const schema = metadataString(metadata, 'databricksSchema') ?? env.DATABRICKS_SCHEMA;
  const resultFormat = metadataString(metadata, 'databricksResultFormat');
  const target: DatabricksSqlSandboxRefData = {
    host: metadataString(metadata, 'databricksHost') ?? env.DATABRICKS_HOST ?? '',
    warehouseId: metadataString(metadata, 'databricksWarehouseId') ?? env.DATABRICKS_WAREHOUSE_ID ?? '',
    ...(catalog ? { catalog } : {}),
    ...(schema ? { schema } : {}),
    ...(resultFormat ? { resultFormat: assertResultFormat(resultFormat) } : {}),
  };
  assertDatabricksSqlSandboxRefData(target);
  return databricksSqlSandbox({
    ...target,
    principal: resolveRegisteredPrincipal(options, target, registration),
    ...(registration.client ? { client: registration.client } : {}),
  });
}

function resolveRegisteredPrincipal(options: SandboxFactoryOptions, target: DatabricksSqlSandboxRefData, registration: DatabricksSqlSandboxRegistrationOptions): DatabricksPrincipal {
  const env = options.env ?? process.env;
  return registration.resolvePrincipal?.(env, target) ?? databricksPrincipalFromEnv(env);
}

function databricksSqlSandboxRefData(options: DatabricksSqlSandboxOptions): DatabricksSqlSandboxRefData {
  return {
    host: options.host,
    warehouseId: options.warehouseId,
    ...(options.catalog ? { catalog: options.catalog } : {}),
    ...(options.schema ? { schema: options.schema } : {}),
    ...(options.resultFormat ? { resultFormat: options.resultFormat } : {}),
    ...(options.waitTimeoutSeconds !== undefined ? { waitTimeoutSeconds: options.waitTimeoutSeconds } : {}),
  };
}

function assertDatabricksSqlSandboxRefData(value: unknown): DatabricksSqlSandboxRefData {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid Databricks SQL sandbox reference.');
  const data = value as Record<string, unknown>;
  if (typeof data.host !== 'string' || data.host.trim().length === 0) throw new Error('Databricks SQL sandbox reference is missing host.');
  if (typeof data.warehouseId !== 'string' || data.warehouseId.trim().length === 0) throw new Error('Databricks SQL sandbox reference is missing warehouseId.');
  const result: DatabricksSqlSandboxRefData = { host: data.host, warehouseId: data.warehouseId };
  if (typeof data.catalog === 'string') result.catalog = data.catalog;
  if (typeof data.schema === 'string') result.schema = data.schema;
  if (data.resultFormat !== undefined) result.resultFormat = assertResultFormat(data.resultFormat);
  if (data.waitTimeoutSeconds !== undefined) {
    if (typeof data.waitTimeoutSeconds !== 'number' || !Number.isFinite(data.waitTimeoutSeconds)) throw new Error('Databricks SQL sandbox reference has an invalid waitTimeoutSeconds.');
    result.waitTimeoutSeconds = data.waitTimeoutSeconds;
  }
  return result;
}

function metadataString(metadata: Record<string, unknown>, key: string): string | undefined {
  const value = metadata[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function assertResultFormat(value: unknown): 'jsonl' | 'csv' {
  if (value === 'jsonl' || value === 'csv') return value;
  throw new Error('Databricks SQL sandbox result format must be jsonl or csv.');
}

function serializeResult(response: StatementResponse, format: 'jsonl' | 'csv'): string {
  const columns = response.manifest?.schema?.columns?.map((c: ColumnInfo) => c.name ?? '') ?? [];
  const rows = (response.result?.dataArray ?? []) as unknown[][];
  if (format === 'csv') {
    const header = columns.join(',');
    const body = rows.map((row) => row.map(formatCsvCell).join(',')).join('\n');
    return [header, body].filter(Boolean).join('\n');
  }
  return rows.map((row) => {
    const obj: Record<string, unknown> = {};
    for (let i = 0; i < columns.length; i += 1) obj[columns[i] ?? `_${i}`] = row[i];
    return JSON.stringify(obj);
  }).join('\n');
}

function formatCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}
